import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "tessellation-wave";

const DEFAULT_BG = "#0e1726";
const DEFAULT_FRONT = "#22d3ee";
const DEFAULT_BACK = "#f472b6";
const DEFAULT_EDGE = "#94a3b8";

// A tiled grid where every pointy-top hexagon continuously rotates around
// its horizontal axis. Each tile shares the same constant rotation rate,
// but receives a phase offset based on its position so the flips form a
// traveling wave across the surface (think: kinetic mechanical sculptures
// where rows of metal tiles flip in unison).
//
// Visual trick for the flip: rotate around the horizontal axis by angle θ.
// In screen space that means scale Y by cos(θ). When cos(θ) > 0 we see the
// front face; when cos(θ) < 0 we see the back face (same geometry, mirrored
// vertically, painted with the back color).
const HEX_SIZE = 22;             // "size" = distance from center to a vertex
const WAVE_DIR_X = 0.85;         // direction the flip wave travels through space
const WAVE_DIR_Y = 0.40;
const WAVE_SPEED = 0.0017;       // radians per ms (rotation rate)
const WAVE_DENSITY = 0.020;      // spatial frequency: how compressed the wave is across tiles
const MIN_VISIBLE_SCALE = 0.04;  // never let a hex collapse fully to a line

// Cache hex polygon offsets (relative to center) once. Pointy-top hex
// vertex angles are at 30°, 90°, 150°, 210°, 270°, 330°.
const HEX_VERTS = [];
for (let i = 0; i < 6; i += 1) {
  const a = (Math.PI / 3) * i + Math.PI / 2; // 90° offset = pointy top
  HEX_VERTS.push({ x: Math.cos(a), y: Math.sin(a) });
}

export default function TessellationWave({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_FRONT, DEFAULT_BACK, DEFAULT_EDGE]);
  const tilesRef = useRef([]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_FRONT),
      pickColor(colors, 2, DEFAULT_BACK),
      pickColor(colors, 3, DEFAULT_EDGE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedTiles = () => {
      // Pointy-top hex grid spacing:
      //   - horizontal spacing = sqrt(3) * size
      //   - vertical spacing   = 1.5 * size (rows are interleaved)
      //   - odd rows are shifted by half the horizontal spacing
      const w = Math.sqrt(3) * HEX_SIZE;
      const h = 1.5 * HEX_SIZE;
      const cols = Math.ceil(width / w) + 2;
      const rows = Math.ceil(height / h) + 2;
      const tiles = [];
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          const xOffset = (r % 2) * (w / 2);
          const cx = c * w + xOffset - w / 2;
          const cy = r * h - h / 2;
          // Per-tile spatial phase = dot(position, waveDir) * density.
          // This is what creates the traveling wave: tiles further along
          // the wave direction get a later "flip moment".
          const phase = (cx * WAVE_DIR_X + cy * WAVE_DIR_Y) * WAVE_DENSITY;
          tiles.push({ cx, cy, phase });
        }
      }
      tilesRef.current = tiles;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedTiles();
    };

    const drawHex = (cx, cy, scaleY, fillColor, edgeColor) => {
      ctx.save();
      ctx.translate(cx, cy);
      // Apply the flip: scale Y by cos(θ). When |scaleY| < 1 the hex
      // appears flatter, simulating its rotation away from the viewer.
      // We use Math.max so the hex never fully collapses (a fully flat
      // hex flickers as a single thin line, which looks wrong).
      const sy = scaleY < 0 ? -Math.max(MIN_VISIBLE_SCALE, -scaleY)
                            :  Math.max(MIN_VISIBLE_SCALE,  scaleY);
      ctx.scale(1, sy);
      ctx.beginPath();
      for (let i = 0; i < HEX_VERTS.length; i += 1) {
        const v = HEX_VERTS[i];
        const px = v.x * HEX_SIZE;
        const py = v.y * HEX_SIZE;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = fillColor;
      ctx.fill();
      ctx.strokeStyle = edgeColor;
      ctx.lineWidth = 0.6 / Math.max(0.2, Math.abs(sy));
      ctx.stroke();
      ctx.restore();
    };

    const tick = (ts) => {
      const [bg, frontCol, backCol, edgeCol] = colorsRef.current;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const tiles = tilesRef.current;
      const wt = ts * WAVE_SPEED;

      // Two-pass approach so back-facing tiles draw under front-facing
      // ones (cleaner visual when neighboring tiles are on different
      // sides of the flip). Cheap because each tile is checked once.
      const edge = rgba(edgeCol, 0.35);

      for (let pass = 0; pass < 2; pass += 1) {
        // pass 0 = back faces (cos < 0), pass 1 = front faces (cos > 0)
        for (let i = 0; i < tiles.length; i += 1) {
          const t = tiles[i];
          // Total angle for this tile: same global rotation rate, but
          // shifted in time by its spatial phase.
          const angle = wt + t.phase;
          const c = Math.cos(angle);
          if (pass === 0 && c >= 0) continue;
          if (pass === 1 && c < 0) continue;
          const fill = c >= 0 ? frontCol : backCol;
          drawHex(t.cx, t.cy, c, fill, edge);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
